import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'lib/api/apiClient';
import { IMINT_URLS } from 'lib/api/endpoints';

export const fetchTargets = createAsyncThunk('imint/fetchTargets', async (_, thunkAPI) => {
  try {
    const response = await apiClient.get(IMINT_URLS.getTargets);
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to fetch locations');
  }
});

// Used by the Create Target flow (next milestone); slice owns it now.
export const createTarget = createAsyncThunk('imint/createTarget', async (payload, thunkAPI) => {
  try {
    const response = await apiClient.post(IMINT_URLS.createTarget, payload);
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to create location');
  }
});

// Delete a location with everything under it (tasks, detections, reports, files).
export const deleteTarget = createAsyncThunk('imint/deleteTarget', async (targetId, thunkAPI) => {
  try {
    await apiClient.delete(IMINT_URLS.deleteTarget(targetId));
    return targetId;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to delete location');
  }
});

// Active detectors for the Create Task picker.
export const fetchDetectors = createAsyncThunk('imint/fetchDetectors', async (_, thunkAPI) => {
  try {
    const response = await apiClient.get(IMINT_URLS.getDetectors);
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to fetch detectors');
  }
});

// Tasks for the selected target (Table View).
export const fetchTasks = createAsyncThunk('imint/fetchTasks', async (targetId, thunkAPI) => {
  try {
    const response = await apiClient.get(IMINT_URLS.getTasks(targetId));
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to fetch tasks');
  }
});

// Run (create) a task from the Create Task modal.
export const createTask = createAsyncThunk('imint/createTask', async (payload, thunkAPI) => {
  try {
    const response = await apiClient.post(IMINT_URLS.createTask, payload);
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to run task');
  }
});

// Saved reports for the selected target (Table View → Reports tab).
export const fetchReports = createAsyncThunk('imint/fetchReports', async (targetId, thunkAPI) => {
  try {
    const response = await apiClient.get(IMINT_URLS.getReports(targetId));
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to fetch reports');
  }
});

const imintSlice = createSlice({
  name: 'imint',
  initialState: {
    targets: [],
    selectedTargetId: null,
    viewMode: 'map', // 'map' | 'tasks' | 'trends' | 'reports' (v20 folder tabs)
    detectors: [],
    tasks: [], // tasks for the selected target
    tasksLoading: false,
    reports: [], // saved reports for the selected target
    loading: false,
    error: null,
  },
  reducers: {
    selectTarget(state, action) {
      state.selectedTargetId = action.payload;
      // viewMode intentionally untouched: switching targets from the table view
      // stays in the table (deselection/creation still land on the map).
      state.tasks = []; // reset — refetched for the newly selected target
      state.reports = [];
    },
    clearSelection(state) {
      state.selectedTargetId = null;
      state.viewMode = 'map';
      state.tasks = [];
      state.reports = [];
    },
    setViewMode(state, action) {
      state.viewMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTargets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTargets.fulfilled, (state, action) => {
        state.loading = false;
        state.targets = action.payload || [];
      })
      .addCase(fetchTargets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTarget.fulfilled, (state, action) => {
        state.targets.push(action.payload);
        state.selectedTargetId = action.payload.id;
        state.viewMode = 'map';
      })
      .addCase(deleteTarget.fulfilled, (state, action) => {
        state.targets = state.targets.filter((t) => t.id !== action.payload);
        if (state.selectedTargetId === action.payload) {
          state.selectedTargetId = null;
          state.viewMode = 'map';
          state.tasks = [];
          state.reports = [];
        }
      })
      .addCase(fetchDetectors.fulfilled, (state, action) => {
        state.detectors = action.payload || [];
      })
      .addCase(fetchTasks.pending, (state) => {
        state.tasksLoading = true;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.tasksLoading = false;
        state.tasks = action.payload || [];
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.tasksLoading = false;
        state.error = action.payload;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        // Newest first (matches the default created-desc sort).
        if (action.payload) state.tasks.unshift(action.payload);
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.reports = action.payload || [];
      });
  },
});

export const { selectTarget, clearSelection, setViewMode } = imintSlice.actions;
export default imintSlice.reducer;
